/**
 * PWA Autista — pagina mobile-first per:
 *   - inserire numero FIR + dati autista/mezzo
 *   - firmare via canvas touch
 *   - salvare firma in backend (presa in carico)
 *   - vedere storico firme per FIR
 *
 * Volutamente fullscreen senza sidebar (vedi route in main.tsx).
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Truck, MapPin, Pen, Save, RotateCcw, ChevronLeft, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/api/client";

function SignaturePad({
  onChange,
  className = "",
}: {
  onChange: (dataUrl: string | null) => void;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const isEmptyRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "#0f172a";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    resize();
    window.addEventListener("resize", resize);

    const getPos = (ev: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
    };
    const onDown = (ev: PointerEvent) => {
      ev.preventDefault();
      drawingRef.current = true;
      const p = getPos(ev);
      lastPosRef.current = p;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const onMove = (ev: PointerEvent) => {
      if (!drawingRef.current) return;
      ev.preventDefault();
      const p = getPos(ev);
      const last = lastPosRef.current!;
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastPosRef.current = p;
      isEmptyRef.current = false;
      onChange(canvas.toDataURL("image/png"));
    };
    const onUp = () => {
      drawingRef.current = false;
      lastPosRef.current = null;
    };

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointerleave", onUp);
    canvas.addEventListener("pointercancel", onUp);

    return () => {
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointerleave", onUp);
      canvas.removeEventListener("pointercancel", onUp);
    };
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    isEmptyRef.current = true;
    onChange(null);
  };

  return (
    <div className={className}>
      <canvas
        ref={canvasRef}
        className="h-56 w-full touch-none rounded-lg border bg-white"
        style={{ touchAction: "none" }}
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Cancella
        </button>
      </div>
    </div>
  );
}

export function AutistaPage() {
  const { firNumero } = useParams<{ firNumero?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [numero, setNumero] = useState(firNumero ?? "");
  useEffect(() => {
    setNumero(firNumero ?? "");
  }, [firNumero]);
  const [autistaNome, setAutistaNome] = useState("");
  const [autistaCf, setAutistaCf] = useState("");
  const [targaMotrice, setTargaMotrice] = useState("");
  const [targaRimorchio, setTargaRimorchio] = useState("");
  const [note, setNote] = useState("");
  const [firma, setFirma] = useState<string | null>(null);
  const [geo, setGeo] = useState<{ lat: number; lon: number } | null>(null);

  // GPS opzionale
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => undefined,
      { enableHighAccuracy: false, timeout: 4000 }
    );
  }, []);

  const firmeQ = useQuery({
    queryKey: ["autista-firme", numero],
    queryFn: () => endpoints.autista.firmePerFir(numero),
    enabled: numero.length > 2,
  });

  const saveMut = useMutation({
    mutationFn: () => {
      if (!numero.trim()) return Promise.reject(new Error("Numero FIR obbligatorio"));
      if (!autistaNome.trim()) return Promise.reject(new Error("Nome autista obbligatorio"));
      if (!firma) return Promise.reject(new Error("Firma obbligatoria"));
      return endpoints.autista.firma(numero, {
        autista_nome: autistaNome,
        autista_cf: autistaCf || undefined,
        targa_motrice: targaMotrice || undefined,
        targa_rimorchio: targaRimorchio || undefined,
        note: note || undefined,
        firma_png_base64: firma,
        geo_lat: geo?.lat,
        geo_lon: geo?.lon,
      });
    },
    onSuccess: () => {
      toast.success("Presa in carico registrata");
      qc.invalidateQueries({ queryKey: ["autista-firme", numero] });
      setFirma(null);
      setNote("");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-12 dark:from-slate-950 dark:to-background">
      <header className="sticky top-0 z-10 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> Suite
          </Link>
          <div className="flex items-center gap-2 font-semibold">
            <Truck className="h-5 w-5 text-primary" /> Modalità autista
          </div>
          <button
            type="button"
            onClick={() => navigate("/autista")}
            className="text-xs text-muted-foreground hover:text-foreground"
            aria-label="Reset"
          >
            Reset
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-md space-y-6 px-4 pt-6">
        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Numero FIR
          </label>
          <input
            inputMode="text"
            autoCapitalize="characters"
            value={numero}
            onChange={(e) => setNumero(e.target.value.toUpperCase())}
            placeholder="Es. FIR-2026-0001"
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base font-mono"
          />
        </section>

        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <h2 className="text-sm font-semibold">Autista</h2>
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Nome e cognome</label>
              <input
                value={autistaNome}
                onChange={(e) => setAutistaNome(e.target.value)}
                placeholder="Mario Rossi"
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Codice fiscale (opz.)</label>
              <input
                value={autistaCf}
                onChange={(e) => setAutistaCf(e.target.value.toUpperCase())}
                placeholder="RSSMRA80A01H501Z"
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base font-mono"
                maxLength={16}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Targa motrice</label>
                <input
                  value={targaMotrice}
                  onChange={(e) => setTargaMotrice(e.target.value.toUpperCase())}
                  placeholder="AB123CD"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Targa rimorchio</label>
                <input
                  value={targaRimorchio}
                  onChange={(e) => setTargaRimorchio(e.target.value.toUpperCase())}
                  placeholder="—"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base font-mono"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Eventuali eccezioni…"
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            {geo && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                {geo.lat.toFixed(5)}, {geo.lon.toFixed(5)}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Pen className="h-4 w-4" /> Firma autista
            </h2>
            <span className="text-xs text-muted-foreground">tocca e disegna</span>
          </div>
          <SignaturePad onChange={setFirma} className="mt-3" />
        </section>

        <button
          type="button"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-base font-semibold text-primary-foreground shadow-md transition hover:bg-primary/90 disabled:opacity-60"
        >
          <Save className="h-5 w-5" />
          {saveMut.isPending ? "Salvataggio…" : "Conferma presa in carico"}
        </button>

        {numero && (
          <section className="rounded-xl border bg-card p-4 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <ListChecks className="h-4 w-4" /> Firme registrate per {numero}
            </h2>
            <div className="mt-3 space-y-2">
              {firmeQ.isLoading && (
                <p className="text-xs text-muted-foreground">Caricamento…</p>
              )}
              {firmeQ.data?.items.length === 0 && (
                <p className="text-xs text-muted-foreground">Nessuna firma ancora.</p>
              )}
              {firmeQ.data?.items.map((it) => (
                <div
                  key={it.id}
                  className="rounded-md border bg-background p-3 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{it.autista}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(it.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {it.targa_motrice && <span>🚛 {it.targa_motrice}</span>}
                    {it.targa_rimorchio && <span>↳ {it.targa_rimorchio}</span>}
                    {it.geo?.lat && it.geo?.lon && (
                      <span>
                        📍 {it.geo.lat.toFixed(4)}, {it.geo.lon.toFixed(4)}
                      </span>
                    )}
                  </div>
                  {it.note && (
                    <p className="mt-1 text-xs text-muted-foreground">📝 {it.note}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
